import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Send, Paperclip, Mic, Square, Trash2, FileText, ArrowLeft, XCircle, Loader2 } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";
import { Avatar } from "./Avatar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hora = (iso) => { try { return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const fmtDur = (s) => { const m = Math.floor((s || 0) / 60); const r = (s || 0) % 60; return `${m}:${String(r).padStart(2, "0")}`; };

// meLado: "familia" | "equipe"; readOnly: admin espectador
export const ChatWindow = ({ conversa, meLado, readOnly = false, onBack, onEncerrar, onChanged, embedded = false }) => {
  const { auth } = useApp();
  const { toast } = useToast();
  const token = auth?.token;
  const cid = conversa?.id;
  const [msgs, setMsgs] = useState([]);
  const [status, setStatus] = useState(conversa?.status || "ativa");
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [gravando, setGravando] = useState(false);
  const [tempoGrav, setTempoGrav] = useState(0);
  const listRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const fileUrl = (path) => `${API}/chat/arquivo?path=${encodeURIComponent(path)}&auth=${encodeURIComponent(token || "")}`;

  const carregar = useCallback(async () => {
    if (!cid) return;
    try {
      const r = await axios.get(`${API}/chat/conversas/${cid}/mensagens`);
      setMsgs(r.data.mensagens || []);
      setStatus(r.data.conversa?.status || "ativa");
      if (onChanged) onChanged();
    } catch (e) { /* ignore */ }
  }, [cid, onChanged]);

  useEffect(() => { carregar(); const t = setInterval(carregar, 6000); return () => clearInterval(t); }, [carregar]);
  useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [msgs]);

  const enviarPayload = async (payload) => {
    try {
      const r = await axios.post(`${API}/chat/conversas/${cid}/mensagens`, payload);
      setMsgs((m) => [...m, r.data]);
      setStatus("ativa");
      if (onChanged) onChanged();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || "Não foi possível enviar.", variant: "destructive" });
    }
  };

  const enviarTexto = async () => {
    const t = texto.trim();
    if (!t || enviando) return;
    setEnviando(true);
    await enviarPayload({ tipo: "texto", texto: t });
    setTexto("");
    setEnviando(false);
  };

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await axios.post(`${API}/chat/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data;
  };

  const anexarArquivo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setEnviando(true);
    try {
      const up = await upload(file);
      const tipo = (up.mime || "").startsWith("image/") ? "imagem" : "arquivo";
      await enviarPayload({ tipo, anexoPath: up.path, anexoNome: up.nome, anexoMime: up.mime, anexoTamanho: up.tamanho });
    } catch (err) {
      toast({ title: err?.response?.data?.detail || "Falha ao enviar o arquivo.", variant: "destructive" });
    }
    setEnviando(false);
  };

  const iniciarGravacao = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (ev) => { if (ev.data.size > 0) chunksRef.current.push(ev.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(timerRef.current);
        const dur = tempoGrav;
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        setTempoGrav(0);
        if (blob.size < 400) return;
        setEnviando(true);
        try {
          const ext = (mr.mimeType || "audio/webm").includes("ogg") ? "ogg" : "webm";
          const file = new File([blob], `audio-${Date.now()}.${ext}`, { type: blob.type });
          const up = await upload(file);
          await enviarPayload({ tipo: "audio", anexoPath: up.path, anexoNome: up.nome, anexoMime: up.mime, anexoTamanho: up.tamanho, duracao: dur });
        } catch (err) {
          toast({ title: "Falha ao enviar o áudio.", variant: "destructive" });
        }
        setEnviando(false);
      };
      recRef.current = mr;
      mr.start();
      setGravando(true);
      setTempoGrav(0);
      timerRef.current = setInterval(() => setTempoGrav((s) => s + 1), 1000);
    } catch (e) {
      toast({ title: "Não foi possível acessar o microfone.", variant: "destructive" });
    }
  };

  const pararGravacao = (cancelar = false) => {
    if (cancelar) { chunksRef.current = []; }
    try { recRef.current?.stop(); } catch (e) { /* ignore */ }
    setGravando(false);
  };

  const excluir = async (m) => {
    try {
      await axios.delete(`${API}/chat/mensagens/${m.id}`);
      setMsgs((x) => x.filter((y) => y.id !== m.id));
      if (onChanged) onChanged();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || "Não foi possível apagar.", variant: "destructive" });
    }
  };

  const encerrar = async () => {
    try {
      await axios.post(`${API}/chat/conversas/${cid}/encerrar`);
      setStatus("encerrada");
      toast({ title: "Conversa encerrada e salva no histórico." });
      if (onEncerrar) onEncerrar();
      if (onChanged) onChanged();
    } catch (e) { toast({ title: "Não foi possível encerrar.", variant: "destructive" }); }
  };

  const tituloOutro = conversa?.nomeExibicao || (meLado === "familia" ? (conversa?.staffNome || conversa?.staffLabel || "Equipe") : (conversa?.familiaNome || conversa?.familiaCpf || "Familiar"));
  const podeEnviar = !readOnly && status !== "encerrada";

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="chat-window">
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-100 bg-white">
        {onBack && <button onClick={onBack} className="p-1 rounded-md hover:bg-slate-100" data-testid="chat-voltar"><ArrowLeft className="w-4 h-4 text-slate-500" /></button>}
        <Avatar nome={tituloOutro} foto={conversa?.fotoExibicao} size={32} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800 truncate">{tituloOutro}</p>
          <p className="text-[11px] text-slate-400 truncate">
            {conversa?.labelExibicao || (meLado === "familia" ? conversa?.staffLabel : (conversa?.residentes?.join(", ") || conversa?.staffLabel))}
            {status === "encerrada" ? " · Encerrada" : ""}
          </p>
        </div>
        {!readOnly && status !== "encerrada" && (
          <Button size="sm" variant="outline" className="gap-1 text-xs text-slate-500 h-8" onClick={encerrar} data-testid="chat-encerrar">
            <XCircle className="w-3.5 h-3.5" /> Encerrar
          </Button>
        )}
      </div>

      <div ref={listRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50/70 min-h-0">
        {msgs.length === 0 && <p className="text-center text-sm text-slate-400 py-8">Nenhuma mensagem ainda.</p>}
        {msgs.map((m) => {
          const meu = m.lado === meLado;
          const podeApagar = !readOnly && meu && !m.lida && status !== "encerrada";
          return (
            <div key={m.id} className={`flex ${meu ? "justify-end" : "justify-start"} gap-1.5 group`} data-testid={`msg-${m.id}`}>
              {!meu && <Avatar nome={m.autorNome} foto={m.autorFoto} size={26} ring="mt-auto" />}
              <div className={`max-w-[78%] rounded-2xl px-3 py-2 ${meu ? "bg-blue-600 text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-700 rounded-bl-sm"}`}>
                {!meu && <p className="text-[11px] font-semibold text-blue-600 mb-0.5">{m.autorNome}{m.autorPerfil === "enfermeiro" ? " (Enfermagem)" : m.autorPerfil === "administrador" ? " (Administração)" : ""}</p>}
                {m.tipo === "imagem" && (
                  <a href={fileUrl(m.anexoPath)} target="_blank" rel="noreferrer">
                    <img src={fileUrl(m.anexoPath)} alt={m.anexoNome} className="rounded-lg max-h-52 object-cover mb-1" data-testid={`msg-img-${m.id}`} />
                  </a>
                )}
                {m.tipo === "arquivo" && (
                  <a href={fileUrl(m.anexoPath)} target="_blank" rel="noreferrer" className={`flex items-center gap-2 text-sm underline ${meu ? "text-white" : "text-blue-600"}`} data-testid={`msg-file-${m.id}`}>
                    <FileText className="w-4 h-4" /> {m.anexoNome || "Documento"}
                  </a>
                )}
                {m.tipo === "audio" && (
                  <div className="flex items-center gap-2">
                    <audio controls src={fileUrl(m.anexoPath)} className="h-9 max-w-[190px]" data-testid={`msg-audio-${m.id}`} />
                    <span className={`text-[10px] ${meu ? "text-blue-100" : "text-slate-400"}`}>{fmtDur(m.duracao)}</span>
                  </div>
                )}
                {m.texto && <p className="text-sm whitespace-pre-wrap break-words">{m.texto}</p>}
                <div className={`flex items-center gap-1.5 mt-0.5 ${meu ? "justify-end" : ""}`}>
                  <span className={`text-[10px] ${meu ? "text-blue-100" : "text-slate-400"}`}>{hora(m.criadoEm)}{meu ? (m.lida ? " · lida" : " · enviada") : ""}</span>
                  {podeApagar && (
                    <button onClick={() => excluir(m)} className="opacity-0 group-hover:opacity-100 transition-opacity" title="Apagar (só antes de ser lida)" data-testid={`msg-del-${m.id}`}>
                      <Trash2 className="w-3 h-3 text-blue-100 hover:text-white" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {readOnly ? (
        <div className="px-3 py-2.5 border-t border-slate-100 bg-slate-50 text-center text-xs text-slate-400" data-testid="chat-somente-leitura">
          Visualização do histórico (somente leitura)
        </div>
      ) : status === "encerrada" ? (
        <div className="px-3 py-2.5 border-t border-slate-100 bg-slate-50 text-center text-xs text-slate-500">
          Conversa encerrada. Envie uma mensagem para reabrir.
          <div className="mt-2 flex items-center gap-2">
            <textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={1} placeholder="Reabrir com uma mensagem..." data-testid="chat-input"
              className="flex-1 px-3 py-2 rounded-lg border border-slate-200 outline-none focus:border-blue-500 text-sm resize-none" />
            <Button onClick={enviarTexto} disabled={enviando || !texto.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="chat-enviar"><Send className="w-4 h-4" /></Button>
          </div>
        </div>
      ) : (
        <div className="p-2.5 border-t border-slate-100 bg-white">
          {gravando ? (
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-2 flex-1 text-sm text-red-600 font-medium">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" /> Gravando {fmtDur(tempoGrav)}
              </span>
              <Button size="sm" variant="ghost" className="text-slate-400" onClick={() => pararGravacao(true)} data-testid="audio-cancelar">Cancelar</Button>
              <Button size="sm" className="bg-red-500 hover:bg-red-600 gap-1" onClick={() => pararGravacao(false)} data-testid="audio-enviar"><Square className="w-3.5 h-3.5" /> Enviar</Button>
            </div>
          ) : (
            <div className="flex items-end gap-1.5">
              <label className="p-2 rounded-lg hover:bg-slate-100 cursor-pointer text-slate-500" title="Anexar imagem ou PDF" data-testid="chat-anexar">
                <Paperclip className="w-5 h-5" />
                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={anexarArquivo} />
              </label>
              <button onClick={iniciarGravacao} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" title="Gravar áudio" data-testid="chat-gravar"><Mic className="w-5 h-5" /></button>
              <textarea
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                rows={1}
                placeholder="Escreva sua mensagem... (Enter = nova linha)"
                data-testid="chat-input"
                className="flex-1 px-3 py-2 rounded-lg border border-slate-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm resize-none max-h-28"
                style={{ minHeight: 40 }}
              />
              <Button onClick={enviarTexto} disabled={enviando || !texto.trim()} className="bg-blue-600 hover:bg-blue-700 h-10 w-10 p-0" data-testid="chat-enviar">
                {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatWindow;
